import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Table, 
  Select, 
  message, 
  Spin,
  Tag,
  Space,
  Statistic,
  Row,
  Col,
  Button
} from 'antd';
import { 
  TrophyOutlined,
  CrownOutlined,
  StarOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import studentAPI from '../../services/studentAPI';
import api from '../../services/api';

const { Option } = Select;

const StudentPointsRanking = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [rankingData, setRankingData] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState(null);
  const [classInfo, setClassInfo] = useState(null);
  const [limit, setLimit] = useState(20);

  useEffect(() => {
    fetchClasses();
  }, []);

  useEffect(() => {
    if (selectedClassId) {
      fetchRankingData();
    }
  }, [selectedClassId, limit]);

  // 获取班级列表
  const fetchClasses = async () => {
    try {
      const response = await api.get('/classes');
      if (response.data.success) {
        const classList = response.data.data;
        setClasses(classList);
        
        // 默认选择第一个班级
        if (classList.length > 0 && !selectedClassId) {
          setSelectedClassId(classList[0].id);
        }
      }
    } catch (error) {
      console.error('获取班级列表失败:', error);
      message.error('获取班级列表失败');
    }
  };

  // 获取排行榜数据
  const fetchRankingData = async () => {
    if (!selectedClassId) return;
    
    setLoading(true);
    try {
      const response = await studentAPI.points.getClassRanking(selectedClassId, { limit });
      
      if (response.data.success) {
        setRankingData(response.data.data.ranking || []);
        setClassInfo(response.data.data.class_info || null);
      } else {
        message.error(response.data.message || '获取排行榜失败');
      }
    } catch (error) {
      console.error('获取排行榜数据失败:', error);
      message.error('获取排行榜数据失败');
    } finally {
      setLoading(false);
    }
  };

  // 刷新数据
  const handleRefresh = () => {
    fetchRankingData();
  };

  // 获取排名图标
  const getRankIcon = (rank) => {
    if (rank === 1) return <CrownOutlined style={{ color: '#FFD700', fontSize: 20 }} />;
    if (rank === 2) return <CrownOutlined style={{ color: '#C0C0C0', fontSize: 18 }} />;
    if (rank === 3) return <CrownOutlined style={{ color: '#CD7F32', fontSize: 16 }} />;
    return null;
  };

  // 获取排名背景色
  const getRankBackground = (rank) => {
    if (rank === 1) return 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)';
    if (rank === 2) return 'linear-gradient(135deg, #C0C0C0 0%, #A8A8A8 100%)';
    if (rank === 3) return 'linear-gradient(135deg, #CD7F32 0%, #8B4513 100%)';
    return '#f0f0f0';
  };

  // 表格列定义
  const columns = [
    {
      title: '排名',
      dataIndex: 'rank',
      key: 'rank',
      width: 80,
      align: 'center',
      render: (rank) => (
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          gap: 8
        }}>
          {getRankIcon(rank)}
          <span style={{ 
            fontWeight: rank <= 3 ? 'bold' : 'normal',
            fontSize: rank <= 3 ? 18 : 14,
            color: rank <= 3 ? '#333' : '#666'
          }}>
            {rank}
          </span>
        </div>
      )
    },
    {
      title: '学生姓名',
      dataIndex: 'student_name',
      key: 'student_name',
      width: 120,
      render: (name, record) => (
        <span style={{ 
          fontWeight: record.rank <= 3 ? 600 : 400,
          fontSize: record.rank <= 3 ? 15 : 14
        }}>
          {name}
        </span>
      )
    },
    {
      title: '总积分',
      dataIndex: 'total_points',
      key: 'total_points',
      width: 120,
      align: 'center',
      sorter: (a, b) => a.total_points - b.total_points,
      render: (points, record) => (
        <Tag 
          color={points > 0 ? 'green' : points < 0 ? 'red' : 'default'}
          style={{ 
            fontSize: record.rank <= 3 ? 16 : 14,
            fontWeight: record.rank <= 3 ? 'bold' : 'normal',
            padding: '4px 12px'
          }}
        >
          {points > 0 ? '+' : ''}{points}
        </Tag>
      )
    },
    {
      title: '加分次数',
      dataIndex: 'earned_count',
      key: 'earned_count',
      width: 100,
      align: 'center',
      render: (count) => (
        <span style={{ color: '#52c41a' }}>{count}次</span>
      )
    },
    {
      title: '扣分次数',
      dataIndex: 'deducted_count',
      key: 'deducted_count',
      width: 100,
      align: 'center',
      render: (count) => (
        <span style={{ color: '#ff4d4f' }}>{count}次</span>
      )
    },
    {
      title: '总记录数',
      dataIndex: 'total_records',
      key: 'total_records',
      width: 100,
      align: 'center'
    },
    {
      title: '最后活动',
      dataIndex: 'last_activity',
      key: 'last_activity',
      width: 160,
      render: (date) => date ? new Date(date).toLocaleString('zh-CN') : '-'
    }
  ];

  // 计算统计数据
  const statistics = {
    totalStudents: rankingData.length,
    topScore: rankingData.length > 0 ? rankingData[0].total_points : 0,
    avgScore: rankingData.length > 0 
      ? (rankingData.reduce((sum, item) => sum + item.total_points, 0) / rankingData.length).toFixed(1)
      : 0
  };

  return (
    <div style={{ padding: '24px' }}>
      <Card 
        title={
          <Space>
            <TrophyOutlined style={{ fontSize: 24, color: '#FFD700' }} />
            <span style={{ fontSize: 18, fontWeight: 600 }}>积分排行榜</span>
          </Space>
        }
        extra={
          <Space>
            <span>班级：</span>
            <Select
              style={{ width: 200 }}
              placeholder="请选择班级"
              value={selectedClassId}
              onChange={setSelectedClassId}
            >
              {classes.map(cls => (
                <Option key={cls.id} value={cls.id}>
                  {cls.class_name}
                </Option>
              ))}
            </Select>
            <span>显示：</span>
            <Select
              style={{ width: 120 }}
              value={limit}
              onChange={setLimit}
            >
              <Option value={10}>前10名</Option>
              <Option value={20}>前20名</Option>
              <Option value={50}>前50名</Option>
              <Option value={100}>前100名</Option>
            </Select>
            <Button 
              icon={<ReloadOutlined />} 
              onClick={handleRefresh}
              loading={loading}
            >
              刷新
            </Button>
          </Space>
        }
      >
        {/* 统计卡片 */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={8}>
            <Card size="small" style={{ background: '#f6ffed', borderColor: '#b7eb8f' }}>
              <Statistic
                title="参与学生数"
                value={statistics.totalStudents}
                prefix={<StarOutlined />}
                suffix="人"
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card size="small" style={{ background: '#fff7e6', borderColor: '#ffd591' }}>
              <Statistic
                title="最高积分"
                value={statistics.topScore}
                prefix={<CrownOutlined />}
                suffix="分"
                valueStyle={{ color: '#fa8c16' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card size="small" style={{ background: '#e6f7ff', borderColor: '#91d5ff' }}>
              <Statistic
                title="平均积分"
                value={statistics.avgScore}
                prefix={<TrophyOutlined />}
                suffix="分"
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
        </Row>

        {/* 排行榜表格 */}
        <Table
          columns={columns}
          dataSource={rankingData}
          rowKey="student_id"
          loading={loading}
          pagination={false}
          scroll={{ x: 800 }}
          rowClassName={(record) => {
            if (record.rank === 1) return 'rank-first';
            if (record.rank === 2) return 'rank-second';
            if (record.rank === 3) return 'rank-third';
            return '';
          }}
        />

        {rankingData.length === 0 && !loading && (
          <div style={{ 
            textAlign: 'center', 
            padding: '40px 0',
            color: '#999'
          }}>
            <TrophyOutlined style={{ fontSize: 48, marginBottom: 16 }} />
            <div>暂无排行榜数据</div>
          </div>
        )}
      </Card>

      {/* 自定义样式 */}
      <style jsx>{`
        .rank-first {
          background: linear-gradient(135deg, rgba(255, 215, 0, 0.1) 0%, rgba(255, 165, 0, 0.05) 100%) !important;
        }
        .rank-second {
          background: linear-gradient(135deg, rgba(192, 192, 192, 0.1) 0%, rgba(168, 168, 168, 0.05) 100%) !important;
        }
        .rank-third {
          background: linear-gradient(135deg, rgba(205, 127, 50, 0.1) 0%, rgba(139, 69, 19, 0.05) 100%) !important;
        }
      `}</style>
    </div>
  );
};

export default StudentPointsRanking;

